import React, { useState, useEffect, useCallback } from 'react';
import { X, Undo2, CheckCircle, Trash2, AlertCircle } from 'lucide-react';

const Toast = ({ message, type = 'info', undoAction, duration = 5000, onDismiss }) => {
    const [visible, setVisible] = useState(false);
    const [progress, setProgress] = useState(100);

    useEffect(() => {
        // Animate in
        setTimeout(() => setVisible(true), 10);

        // Progress bar countdown
        const interval = setInterval(() => {
            setProgress(prev => {
                const next = prev - (100 / (duration / 50));
                return next <= 0 ? 0 : next;
            });
        }, 50);

        // Auto dismiss
        const timer = setTimeout(() => {
            handleDismiss();
        }, duration);

        return () => {
            clearInterval(interval);
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration]);

    const handleDismiss = useCallback(() => {
        setVisible(false);
        setTimeout(() => onDismiss?.(), 300);
    }, [onDismiss]);

    const handleUndo = () => {
        undoAction?.();
        handleDismiss();
    };

    const typeStyles = {
        success: 'bg-green-600',
        error: 'bg-red-600',
        info: 'bg-gray-800',
        delete: 'bg-red-600',
    };

    const typeIcons = {
        success: <CheckCircle className="h-5 w-5" />,
        error: <AlertCircle className="h-5 w-5" />,
        info: <CheckCircle className="h-5 w-5" />,
        delete: <Trash2 className="h-5 w-5" />,
    };

    return (
        <div
            className={`fixed bottom-24 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-96 z-[60] transform transition-all duration-300 ease-out ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
        >
            <div className={`${typeStyles[type]} rounded-lg shadow-xl overflow-hidden`}>
                <div className="px-4 py-3 flex items-center justify-between text-white">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {typeIcons[type]}
                        <p className="text-sm font-medium truncate">{message}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                        {undoAction && (
                            <button
                                onClick={handleUndo}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md text-sm font-medium transition-colors"
                            >
                                <Undo2 className="h-4 w-4" />
                                <span>Undo</span>
                            </button>
                        )}
                        <button
                            onClick={handleDismiss}
                            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                {/* Progress bar */}
                <div className="h-1 bg-white bg-opacity-20">
                    <div
                        className="h-full bg-white bg-opacity-50 transition-all duration-50 ease-linear"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

// Toast container that manages multiple toasts
const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <>
            {toasts.map((toast, index) => (
                <div
                    key={toast.id}
                    style={{ transform: `translateY(-${index * 80}px)` }}
                    className="transition-transform duration-300"
                >
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        undoAction={toast.undoAction}
                        duration={toast.duration}
                        onDismiss={() => removeToast(toast.id)}
                    />
                </div>
            ))}
        </>
    );
};

export { Toast, ToastContainer };
export default Toast;
